/**
 * Voice AI — Shared Phone Styles
 * Imported by all phone sub-components via:
 *   import { phoneStyles } from '../../styles/phone-styles.js';
 *   static styles = [designSystem, componentStyles, phoneStyles];
 */
import { css } from 'lit';

export const phoneStyles = css`

  /* ═══════════════════════════════════════════════
     PAGE HEADER & STATS
     Used by: phone-number-table
     ═══════════════════════════════════════════════ */

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-8);
  }
  .page-header-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .page-title {
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
  }
  .page-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-4);
    margin-bottom: var(--space-8);
  }
  .stat-card {
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    display: flex;
    align-items: center;
    gap: var(--space-4);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
    overflow: hidden;
  }
  .stat-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    flex-shrink: 0;
  }
  .stat-icon.total { background: var(--color-primary-light); color: var(--color-primary); }
  .stat-icon.active { background: var(--color-success-light); color: var(--color-success); }
  .stat-icon.cost { background: var(--color-accent-light); color: var(--color-accent); }
  .stat-content { display: flex; flex-direction: column; gap: 2px; }

  /* ═══════════════════════════════════════════════
     TABLE
     Used by: phone-number-table
     ═══════════════════════════════════════════════ */

  .table-container {
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    position: relative;
  }
  .table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) var(--space-5);
    border-bottom: 1px solid var(--color-border-primary);
  }
  .table-title { font-family: var(--font-display); font-size: var(--text-base); font-weight: var(--font-semibold); color: var(--color-text-primary); }
  .table-scroll { overflow-x: auto; }

  /* Table cell customizations */
  .data-table thead th:first-child { border-radius: 0; }
  .data-table thead th:last-child { border-radius: 0; }
  .phone-cell { display: flex; align-items: center; gap: var(--space-2); }
  .phone-flag { font-size: var(--text-lg); line-height: 1; }
  .phone-number { font-family: var(--font-mono); font-size: var(--text-sm); font-weight: var(--font-medium); color: var(--color-text-primary); letter-spacing: 0.02em; }
  .agent-cell { display: flex; align-items: center; gap: var(--space-2); }
  .agent-mini-avatar { width: 24px; height: 24px; border-radius: var(--radius-full); background: linear-gradient(135deg, var(--color-primary-subtle), var(--color-primary-light)); display: flex; align-items: center; justify-content: center; font-size: var(--text-xs); color: var(--color-primary); font-weight: var(--font-semibold); flex-shrink: 0; }
  .agent-name-text { font-size: var(--text-sm); color: var(--color-text-secondary); }
  .label-text { font-size: var(--text-sm); font-weight: var(--font-medium); color: var(--color-text-primary); }
  .park-text { font-family: var(--font-mono); font-size: var(--text-xs); color: var(--color-text-tertiary); background: var(--color-bg-tertiary); padding: 2px 6px; border-radius: var(--radius-sm); }
  .cost-text { font-family: var(--font-mono); font-size: var(--text-sm); color: var(--color-text-secondary); }
  .actions-cell { display: flex; align-items: center; gap: var(--space-1); }
  .action-btn { width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: none; background: transparent; color: var(--color-text-tertiary); border-radius: var(--radius-md); cursor: pointer; transition: all var(--transition-fast); font-size: var(--text-sm); }
  .action-btn:hover { background: var(--color-bg-tertiary); color: var(--color-text-primary); }
  .action-btn.danger:hover { background: var(--color-error-light); color: var(--color-error); }

  /* Clickable rows */
  .data-table tbody tr { cursor: pointer; }
  .data-table tbody tr.expanded { background: var(--color-bg-hover); }
  .data-table tbody tr.expanded td { border-bottom-color: transparent; }

  /* Sort indicator */
  .sortable { cursor: pointer; user-select: none; }
  .sortable:hover { color: var(--color-text-primary); }
  .sort-icon { margin-left: 4px; font-size: var(--text-xs); opacity: 0.5; }
  .sort-icon.active { opacity: 1; color: var(--color-accent); }

  /* Loading / Empty */
  .loading-row td, .empty-row td { text-align: center; padding: var(--space-8) !important; color: var(--color-text-tertiary); font-size: var(--text-sm); }

  /* Skeleton loading uses shared styles from design-system.js */

  /* ═══════════════════════════════════════════════
     EXPANDED ROW DETAIL
     Used by: phone-number-table
     ═══════════════════════════════════════════════ */

  .expand-row td { padding: 0 !important; border-bottom: 1px solid var(--color-border-primary) !important; }
  .expand-content { padding: var(--space-4) var(--space-5); padding-left: calc(var(--space-5) + var(--space-8)); background: var(--color-bg-tertiary); animation: slideDown var(--duration-base) var(--ease-out) both; }
  .expand-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-5); }
  .expand-section { display: flex; flex-direction: column; gap: var(--space-2); }
  .expand-section-title { font-family: var(--font-body); font-size: var(--text-xs); font-weight: var(--font-semibold); color: var(--color-text-tertiary); text-transform: uppercase; letter-spacing: var(--tracking-wider); }
  .expand-section-value { font-family: var(--font-body); font-size: var(--text-sm); color: var(--color-text-primary); line-height: var(--leading-relaxed); }
  .hours-list { display: flex; flex-direction: column; gap: var(--space-1); }
  .hours-item { display: flex; align-items: center; gap: var(--space-2); font-size: var(--text-sm); color: var(--color-text-secondary); }
  .hours-day { width: 32px; font-weight: var(--font-medium); color: var(--color-text-primary); font-size: var(--text-xs); text-transform: uppercase; }
  .hours-time { font-family: var(--font-mono); font-size: var(--text-xs); color: var(--color-text-secondary); }
  .notes-textarea { width: 100%; min-height: 60px; padding: var(--space-2) var(--space-3); background: var(--color-bg-secondary); border: 1px solid var(--color-border-primary); border-radius: var(--radius-md); color: var(--color-text-primary); font-family: var(--font-body); font-size: var(--text-sm); resize: vertical; transition: all var(--transition-fast); line-height: var(--leading-normal); }
  .notes-textarea:focus { outline: none; border-color: var(--color-primary); box-shadow: var(--shadow-glow-primary); }
  .agent-select { padding: var(--space-1) var(--space-2); font-size: var(--text-xs); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-surface); color: var(--color-text-primary); cursor: pointer; }
  .agent-select:focus { outline: none; border-color: var(--color-primary); }
  .caller-id-row { display: flex; align-items: center; gap: var(--space-2); font-size: var(--text-sm); color: var(--color-text-secondary); }
  .caller-id-row i { color: var(--color-success); font-size: var(--text-xs); }

  /* ═══════════════════════════════════════════════
     PAGINATION
     Used by: phone-number-table
     ═══════════════════════════════════════════════ */

  .pagination-bar { display: flex; align-items: center; justify-content: space-between; padding: var(--space-3) var(--space-5); border-top: 1px solid var(--color-border-primary); }
  .pagination-info { font-size: var(--text-sm); color: var(--color-text-tertiary); }
  .pagination-controls { display: flex; align-items: center; gap: var(--space-1); }
  .page-btn { min-width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border: 1px solid transparent; border-radius: var(--radius-md); background: transparent; color: var(--color-text-secondary); font-size: var(--text-sm); cursor: pointer; transition: all var(--duration-fast); }
  .page-btn:hover:not(:disabled):not(.active) { background: var(--color-bg-tertiary); }
  .page-btn.active { background: var(--color-primary); color: white; border-color: var(--color-primary); }
  .page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .page-ellipsis { min-width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: var(--color-text-tertiary); font-size: var(--text-sm); }

  /* Modal/dialog overlays now handled by <voice-modal> component */

  /* ═══════════════════════════════════════════════
     TWILIO NUMBER LIST (Import Modal)
     Used by: phone-page
     ═══════════════════════════════════════════════ */

  .twilio-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .twilio-list-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--duration-fast) var(--ease-out);
  }
  .twilio-list-item:hover:not(.disabled) {
    border-color: var(--color-accent);
    background: color-mix(in srgb, var(--color-accent) 5%, transparent);
  }
  .twilio-list-item.selected {
    border-color: var(--color-accent);
    background: color-mix(in srgb, var(--color-accent) 8%, transparent);
    box-shadow: 0 0 0 1px var(--color-accent);
  }
  .twilio-list-item.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .twilio-list-radio {
    width: 22px;
    height: 22px;
    border: 2px solid var(--color-border-secondary);
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color var(--duration-fast);
  }
  .twilio-list-item.selected .twilio-list-radio {
    border-color: var(--color-accent);
  }
  .twilio-list-item.selected .twilio-list-radio::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--color-accent);
  }
  .twilio-list-info { flex: 1; min-width: 0; }
  .twilio-list-phone {
    font-family: var(--font-mono);
    font-size: var(--text-base);
    font-weight: var(--font-semibold);
    color: var(--color-text-primary);
  }
  .twilio-list-name {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .twilio-list-caps { display: flex; gap: var(--space-1); flex-shrink: 0; }
  .twilio-cap-badge {
    font-size: 10px;
    font-weight: var(--font-semibold);
    padding: 2px 6px;
    border-radius: var(--radius-sm);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .twilio-cap-badge.voice { background: color-mix(in srgb, #10B981 15%, transparent); color: #059669; }
  .twilio-cap-badge.sms { background: color-mix(in srgb, #3B82F6 15%, transparent); color: #2563EB; }
  .twilio-cap-badge.mms { background: color-mix(in srgb, #8B5CF6 15%, transparent); color: #7C3AED; }
  .twilio-cap-badge.imported { background: color-mix(in srgb, var(--color-text-tertiary) 15%, transparent); color: var(--color-text-tertiary); }

  .twilio-loading, .twilio-empty, .twilio-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-8) var(--space-4);
    gap: var(--space-3);
    color: var(--color-text-secondary);
    text-align: center;
  }
  .twilio-loading i, .twilio-empty i, .twilio-error i {
    font-size: var(--text-2xl);
    opacity: 0.5;
  }

  /* Confirm dialogs now handled by <voice-modal> component */

  /* ═══════════════════════════════════════════════
     BUTTONS (shared across phone components)
     ═══════════════════════════════════════════════ */

  .btn-back {
    margin-right: auto;
    display: flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-3);
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
    cursor: pointer;
    border-radius: var(--radius-md);
  }
  .btn-back:hover {
    color: var(--color-text-primary);
    background: var(--color-bg-tertiary);
  }
  .btn-cancel {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-primary);
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    cursor: pointer;
  }
  .btn-cancel:hover { background: var(--color-bg-hover); }

  /* ═══════════════════════════════════════════════
     FORM FIELDS
     Used by: phone-number-modal
     ═══════════════════════════════════════════════ */

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-4);
  }
  /* Form base styles inherited from componentStyles (design-system.js) */
  /* Phone-specific overrides: uppercase labels */
  .form-label {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
  }
  .form-section-title {
    font-size: var(--text-xs);
    font-weight: var(--font-bold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
    padding-top: var(--space-3);
    padding-bottom: var(--space-1);
    border-top: 1px solid var(--color-border-primary);
  }

  /* ═══════════════════════════════════════════════
     TEST CALL DIALOG
     Used by: phone-test-modal
     ═══════════════════════════════════════════════ */

  .test-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-full);
    background: color-mix(in srgb, #10B981 12%, transparent);
    color: #059669;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-xl);
    margin: 0 auto var(--space-4);
  }
  .test-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    text-align: center;
    margin-bottom: var(--space-4);
  }
  .test-from {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-md);
    margin-bottom: var(--space-3);
  }
  .test-from-label {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .test-from-number {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
    margin-left: auto;
  }
  .test-input-group { margin-bottom: var(--space-4); }
  .test-input-group label {
    display: block;
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: var(--space-1);
  }
  .test-input {
    width: 100%;
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    background: var(--color-bg-primary);
    transition: border-color var(--duration-fast);
    box-sizing: border-box;
  }
  .test-input:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-accent) 20%, transparent);
  }
  .test-input.error {
    border-color: var(--color-error);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-error) 15%, transparent);
  }
  .test-hint { font-size: var(--text-xs); color: var(--color-text-tertiary); margin-top: var(--space-1); }
  .test-error-hint { font-size: var(--text-xs); color: var(--color-error); margin-top: var(--space-1); }
  .test-result {
    padding: var(--space-3);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    margin-bottom: var(--space-4);
    line-height: var(--leading-relaxed);
  }
  .test-result.success {
    background: color-mix(in srgb, #10B981 10%, transparent);
    border: 1px solid color-mix(in srgb, #10B981 25%, transparent);
    color: #065F46;
  }
  .test-result.error {
    background: color-mix(in srgb, var(--color-error) 10%, transparent);
    border: 1px solid color-mix(in srgb, var(--color-error) 25%, transparent);
    color: var(--color-error);
  }
  .test-result-sid {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-top: var(--space-1);
    word-break: break-all;
  }
  .btn-call {
    background: #059669;
    color: white;
    border: none;
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    transition: opacity var(--duration-fast);
  }
  .btn-call:hover:not(:disabled) { opacity: 0.9; }
  .btn-call:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ═══════════════════════════════════════════════
     TOAST
     Used by: phone-page
     ═══════════════════════════════════════════════ */

  .toast {
    position: fixed;
    bottom: var(--space-6);
    right: var(--space-6);
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-lg);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: white;
    z-index: var(--z-toast);
    animation: slideUp var(--duration-base) var(--ease-out) both;
    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .toast.success { background: var(--color-success); }
  .toast.error { background: var(--color-error); }

  /* ═══════════════════════════════════════════════
     RESPONSIVE
     ═══════════════════════════════════════════════ */

  @media (max-width: 1024px) {
    .stats-row { grid-template-columns: repeat(2, 1fr); }
    .expand-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 768px) {
    .page-header { flex-direction: column; gap: var(--space-4); }
    .stats-row { grid-template-columns: 1fr; }
    .expand-grid { grid-template-columns: 1fr; }
    .search-input-wrap { max-width: none; }
  }
`;
